import { useState } from "react";
import { fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";

const SCALES = { Mois: 4, Semaine: 12, Jour: 30 };
const DAY = 86400000;
const iso = (d) => new Date(d + "T00:00:00");
const color = (t) => t.en_retard ? "bg-rose-500" : t.statut === "Terminée" ? "bg-emerald-500" : t.statut === "Bloquée" ? "bg-amber-500" : t.statut === "En cours" ? "bg-brand-600" : "bg-slate-400";

function Bar({ left, width, cls, progress, label, testid, title }) {
  return (
    <div className={`absolute top-1.5 h-5 rounded ${cls} overflow-hidden shadow-sm`} style={{ left, width: Math.max(width, 6) }} title={title} data-testid={testid}>
      {progress != null && <div className="absolute inset-y-0 left-0 bg-white/30" style={{ width: `${progress}%` }} />}
      <span className="relative px-1.5 text-[10px] leading-5 text-white font-semibold whitespace-nowrap">{label}</span>
    </div>
  );
}

export default function TechGantt({ phases, tasks }) {
  const [scale, setScale] = useState("Semaine");
  const dated = tasks.filter((t) => t.date_debut && t.date_fin);
  if (!dated.length) return <p className="text-sm text-slate-400 py-10 text-center rounded-lg border border-dashed" data-testid="tech-gantt-empty">Renseignez les dates de début et de fin des tâches pour afficher le planning.</p>;
  const todayD = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00");
  const all = dated.flatMap((t) => [iso(t.date_debut), iso(t.date_fin)]);
  const min = new Date(Math.min(...all) - 2 * DAY);
  const max = new Date(Math.max(...all, todayD) + 3 * DAY);
  const px = SCALES[scale];
  const off = (d) => Math.round((d - min) / DAY) * px;
  const W = off(max) + 20;
  const months = [];
  for (let c = new Date(min.getFullYear(), min.getMonth(), 1); c <= max; c = new Date(c.getFullYear(), c.getMonth() + 1, 1)) months.push(new Date(c));
  const groups = [...phases.map((p) => ({ p, ts: dated.filter((t) => t.phase_id === p.id) })),
    { p: null, ts: dated.filter((t) => !phases.some((p) => p.id === t.phase_id)) }].filter((g) => g.ts.length);
  const lateCount = dated.filter((t) => t.en_retard).length;
  return (
    <div className="space-y-3" data-testid="tech-gantt">
      <div className="flex items-center gap-3 flex-wrap text-xs">
        <div className="flex rounded-md border overflow-hidden">{Object.keys(SCALES).map((s) => (
          <Button key={s} size="sm" variant={s === scale ? "default" : "ghost"} className={`h-7 rounded-none ${s === scale ? "bg-brand-600 text-white" : ""}`} onClick={() => setScale(s)} data-testid={`tech-gantt-scale-${s}`}>{s}</Button>))}</div>
        {[["bg-rose-500", "En retard"], ["bg-brand-600", "En cours"], ["bg-emerald-500", "Terminée"], ["bg-amber-500", "Bloquée"], ["bg-slate-400", "À faire"]].map(([c, l]) => <span key={l} className="inline-flex items-center gap-1"><i className={`h-2.5 w-2.5 rounded-sm ${c}`} />{l}</span>)}
        <span className={`ml-auto font-semibold ${lateCount ? "text-rose-600" : "text-slate-500"}`} data-testid="tech-gantt-late">{lateCount} tâche(s) en retard</span>
      </div>
      <div className="rounded-lg border bg-white overflow-x-auto scrollbar-thin">
        <div className="flex min-w-max">
          <div className="w-44 sm:w-56 shrink-0 border-r bg-white sticky left-0 z-10">
            <div className="h-8 border-b bg-slate-50" />
            {groups.map(({ p, ts }) => (
              <div key={p?.id || "none"}>
                <div className="h-8 px-3 flex items-center text-xs font-bold bg-slate-50 border-b truncate">{p ? p.nom : "Sans phase"}{p && <span className="ml-auto font-mono text-slate-400">{p.avancement}%</span>}</div>
                {ts.map((t) => <div key={t.id} className={`h-8 px-3 flex items-center text-xs border-b truncate ${t.en_retard ? "text-rose-600 font-semibold" : "text-slate-600"}`}>{t.titre}</div>)}
              </div>
            ))}
          </div>
          <div className="relative" style={{ width: W }}>
            <div className="h-8 border-b bg-slate-50 relative">
              {months.map((m) => <span key={m.toISOString()} className="absolute top-0 h-8 border-l px-1 text-[10px] text-slate-500 leading-8 capitalize" style={{ left: Math.max(0, off(m)) }}>{m.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" })}</span>)}
            </div>
            <div className="absolute top-8 bottom-0 w-0.5 bg-rose-400 z-[5]" style={{ left: off(todayD) }} data-testid="tech-gantt-today"><span className="absolute -top-0 left-1 text-[9px] text-rose-500 font-bold">Auj.</span></div>
            {groups.map(({ p, ts }) => {
              const s = new Date(Math.min(...ts.map((t) => iso(t.date_debut)))); const e = new Date(Math.max(...ts.map((t) => iso(t.date_fin))));
              return (
                <div key={p?.id || "none"}>
                  <div className="h-8 relative border-b bg-slate-50/60"><Bar left={off(s)} width={off(e) - off(s) + px} cls="bg-slate-700" progress={p?.avancement ?? null} label={p ? `${p.avancement}%` : ""} testid={`tech-gantt-phase-${p?.id || "none"}`} /></div>
                  {ts.map((t) => (
                    <div key={t.id} className="h-8 relative border-b">
                      <Bar left={off(iso(t.date_debut))} width={off(iso(t.date_fin)) - off(iso(t.date_debut)) + px} cls={color(t)} progress={t.avancement} label={`${t.avancement}%`}
                        title={`${t.titre} · ${fmtDate(t.date_debut)} → ${fmtDate(t.date_fin)} · ${t.statut}${t.en_retard ? " · EN RETARD" : ""}`} testid={`tech-gantt-task-${t.id}`} />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
